import React, { useState, useEffect } from 'react';
import { Search, Building2, MapPin, Map } from 'lucide-react';
import { motion } from 'motion/react';

interface SchoolData {
  "UDIES CODE": string;
  "SCHOOL NAME": string;
  "BLOCK": string;
  "DISTRICT": string;
}

export const SearchSchoolView: React.FC = () => {
  const [udiseCode, setUdiseCode] = useState('');
  const [schoolData, setSchoolData] = useState<SchoolData | null>(null);
  const [allSchools, setAllSchools] = useState<SchoolData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const response = await fetch('/api/schools');
        if (!response.ok) {
          throw new Error('Failed to load school data');
        }
        const data = await response.json();
        setAllSchools(data);
      } catch (err) {
        console.error("Error loading schools:", err);
      }
    };
    fetchSchools();
  }, []);

  const handleSearch = () => {
    setError(null);
    setSchoolData(null);
    
    if (!udiseCode.trim()) {
      setError("Please enter a UDISE code");
      return;
    }

    setIsLoading(true);
    
    // Simulate network delay for better UX
    setTimeout(() => {
      const foundSchool = allSchools.find(s => s["UDIES CODE"].trim() === udiseCode.trim());
      
      if (foundSchool) {
        setSchoolData(foundSchool);
      } else {
        setError("No school found with this UDISE code");
      }
      setIsLoading(false);
    }, 300);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
        <div className="bg-gradient-to-r from-orange-600 to-red-700 p-8 md:p-12 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
            <Building2 size={120} className="text-white" />
          </div>
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4 tracking-tight">
              Search Your School
            </h2>
            <p className="text-orange-100 text-lg max-w-2xl mx-auto">
              Enter your 11-digit UDISE code to find your school details instantly.
            </p>
          </div>
        </div>

        <div className="p-8 md:p-12">
          <div className="max-w-2xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={udiseCode}
                  onChange={(e) => setUdiseCode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Enter UDISE Code (e.g. 10151000102)"
                  className="block w-full pl-11 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-lg font-medium"
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={isLoading}
                className="px-8 py-4 bg-gray-900 text-white rounded-2xl font-bold text-lg hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center min-w-[140px]"
              >
                {isLoading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  'Search'
                )}
              </button>
            </div>

            {error && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 p-4 bg-red-50 text-red-600 rounded-xl text-center font-medium border border-red-100"
              >
                {error}
              </motion.div>
            )}

            {schoolData && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 space-y-4"
              >
                <div className="bg-orange-50 border border-orange-100 rounded-2xl p-6 md:p-8">
                  <h3 className="text-sm font-bold text-orange-600 uppercase tracking-wider mb-6 flex items-center">
                    <Building2 className="w-4 h-4 mr-2" />
                    School Details
                  </h3>
                  
                  <div className="space-y-6">
                    <div>
                      <p className="text-sm text-gray-500 font-medium mb-1">School Name</p>
                      <p className="text-xl md:text-2xl font-black text-gray-900">{schoolData["SCHOOL NAME"]}</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-orange-200/50">
                      <div className="flex items-start">
                        <div className="bg-white p-2 rounded-lg shadow-sm mr-4">
                          <MapPin className="w-5 h-5 text-orange-500" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-1 uppercase tracking-wider">Block</p>
                          <p className="text-lg font-bold text-gray-900">{schoolData["BLOCK"]}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="bg-white p-2 rounded-lg shadow-sm mr-4">
                          <Map className="w-5 h-5 text-orange-500" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-1 uppercase tracking-wider">District</p>
                          <p className="text-lg font-bold text-gray-900">{schoolData["DISTRICT"]}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
